import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Demo countdown banner component
 * Shows remaining time and upgrade CTA for demo accounts
 * Cannot be dismissed - always visible during demo period
 */
export const DemoBanner = ({ expiresAt, onExpired }) => {
  const { signOut } = useAuth();
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isExpired, setIsExpired] = useState(false);

  // Calculate time remaining
  const calculateTimeRemaining = useCallback(() => {
    if (!expiresAt) return null;

    const now = new Date().getTime();
    const expiry = new Date(expiresAt).getTime();
    const diff = expiry - now;

    if (diff <= 0) {
      setIsExpired(true);
      onExpired?.();
      return { hours: 0, minutes: 0, seconds: 0, total: 0 };
    }

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return { hours, minutes, seconds, total: diff };
  }, [expiresAt, onExpired]);

  // Update countdown every second
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(calculateTimeRemaining());
    }, 1000);

    // Initial calculation
    setTimeRemaining(calculateTimeRemaining());

    return () => clearInterval(timer);
  }, [calculateTimeRemaining]);

  // Don't render if no expiry
  if (!expiresAt || !timeRemaining) {
    return null;
  }

  // Format time display
  const formatTime = () => {
    if (isExpired) return '00:00:00';

    const { hours, minutes, seconds } = timeRemaining;
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  };

  // Get urgency level
  const isUrgent = timeRemaining.total < 1000 * 60 * 60; // Less than 1 hour

  return (
    <div className={`
      ${isExpired ? 'bg-red-600' : 'bg-black'}
      text-white
    `}>
      <div className="px-4 py-2">
        <div className="max-w-7xl mx-auto flex items-center justify-end gap-4">
          {/* Demo badge and timer */}
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-white/70">DEMO</span>
            <span className="text-white/40">|</span>
            <span className={`font-mono text-sm ${isUrgent ? 'text-red-400' : 'text-white/90'}`}>
              {isExpired ? 'Expired' : formatTime()}
            </span>
          </div>

          {/* CTA button */}
          <Button
            size="sm"
            className="bg-white text-black hover:bg-white/90 text-xs"
            onClick={async () => {
              await signOut();
              window.location.href = '/signup';
            }}
          >
            Get Full Access
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DemoBanner;
